import React, { useEffect, useId, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    CloseButton,
    Dropdown,
    Form,
    Modal,
    Spinner,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import LaContractTask from '@/components/common/LaContractTask';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import ReleaseReasonDialog from '@/components/hiring/ReleaseReasonDialog';
import useRevealAlert from '@/shared/hooks/useRevealAlert';
import {
    usePreviewLock,
    PREVIEW_LOCK_NOTE_ID,
} from '@/shared/hooks/usePreviewLock';
import { candidacyService } from '@/shared/services/candidacyService';
import { formatAnswerBy, formatAnswerDay } from '@/shared/utils/deadline';
import { modalityText, positionHeading } from '@/shared/utils/positionText';
import {
    acceptItems,
    formatInterviewWhen,
    positionShortName,
    sessionsFor,
} from '@/shared/utils/studentPositions';
import { applicableCommitments } from './programs/ProgramCommitments';
import './StudentPositions.css';

const OFFER_CHANGED_MESSAGE = 'This offer changed. Reload to see it.';
// Refusals the server names by `code` that say the offer on screen is no
// longer the live one; Reload replaces Accept for them. A changed offer is
// known by its message.
const STALE_OFFER_CODES = ['offer_expired', 'offer_taken_back'];
const FALLBACK_ERROR = 'Your answer could not be sent. Try again.';
export const formatDate = formatAnswerDay;

export const formatDateTime = formatInterviewWhen;

// The weekly total this accept would reach and the limit that applies to the
// student. The total is the hours already held plus this position's hours:
// the commitment's offered hours already include the offer being answered.
function hoursPicture(commitment, program) {
    const hours =
        commitment?.programs?.[program]?.hoursPerWeekPerPosition ?? null;
    if (hours === null) {
        return { hours, total: null, limit: null, over: false, holds: false };
    }
    const total = commitment.heldHours + hours;
    return {
        hours,
        total,
        limit: commitment.limit,
        over: total > commitment.limit,
        holds: commitment.positions >= 1,
    };
}

function IfYouAccept({ items, commitments }) {
    return (
        <>
            <h3 className="h6">If you accept</h3>
            <ul className="mb-2">
                {items.map((item) => (
                    <li key={item}>{item}</li>
                ))}
            </ul>
            {commitments.length > 0 && (
                <details className="mb-2">
                    <summary>
                        The commitments you agreed to when you applied
                    </summary>
                    <ol className="mb-0 mt-2">
                        {commitments.map(({ text }) => (
                            <li key={text}>{text}</li>
                        ))}
                    </ol>
                </details>
            )}
        </>
    );
}

// What the position, the student's commitment and the program's own dates say
// accepting means. The student's new-or-returning answer picks the dates.
function acceptDetails({ candidacy, picture, commitment, acceptInfo, others }) {
    const isReturning = acceptInfo?.isReturning;
    const sessions = sessionsFor(acceptInfo?.sessions, isReturning);
    const items = acceptItems({
        position: candidacy.position,
        picture,
        program: candidacy.program,
        programLimits: commitment?.programs?.[candidacy.program],
        sessions,
        otherOffers: others,
    });
    const commitments = applicableCommitments(
        acceptInfo?.commitments,
        isReturning
    );
    return {
        items,
        commitments,
        training: sessions.map(({ title, when }) => `${title}: ${when}.`),
    };
}

function AcceptDialog({
    heading,
    picture,
    locked,
    candidacy,
    details,
    onDone,
    onCancel,
    onChanged,
}) {
    const titleId = useId();
    const descriptionId = useId();
    const checkId = useId();
    const [checked, setChecked] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [stale, setStale] = useState(false);
    const inFlight = useRef(false);
    const errorRef = useRevealAlert(error);
    const needsConfirm = picture.total !== null && picture.holds;
    const offerChanged = stale || error === OFFER_CHANGED_MESSAGE;

    const close = () => {
        if (!inFlight.current) onCancel();
    };

    const confirm = async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        setSubmitting(true);
        setError(null);
        try {
            const updated = await candidacyService.respond(
                candidacy.id,
                'accept',
                candidacy.offer.offeredAt
            );
            onDone(updated);
        } catch (err) {
            setStale(STALE_OFFER_CODES.includes(err?.response?.data?.code));
            setError(err?.response?.data?.message || FALLBACK_ERROR);
        } finally {
            inFlight.current = false;
            setSubmitting(false);
        }
    };

    const acceptBlocked = picture.over || (needsConfirm && !checked);

    return (
        <Modal
            show
            onHide={close}
            animation={false}
            restoreFocus
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
        >
            <Modal.Header>
                <Modal.Title id={titleId} as="h2" className="h5">
                    Accept {heading}?
                </Modal.Title>
                <CloseButton
                    aria-label="Close"
                    onClick={close}
                    disabled={submitting}
                />
            </Modal.Header>
            <Modal.Body>
                <div id={descriptionId}>
                    <p>
                        {`You'll be the Learning Assistant for ${heading} this term.`}
                    </p>
                    <IfYouAccept
                        items={details.items}
                        commitments={details.commitments}
                    />
                    <p>
                        If you need to leave later, use More on the position;
                        the program is told.
                    </p>
                    {picture.over && (
                        <p className="fw-semibold">
                            That is over your limit, so you can&apos;t accept
                            this offer while you hold your other positions.
                            Contact the LA program if your hours have changed.
                        </p>
                    )}
                </div>
                {needsConfirm && (
                    <Form.Check
                        id={checkId}
                        type="checkbox"
                        className="mt-3"
                        label="I can work these hours alongside my other campus duties."
                        checked={checked}
                        onChange={(event) => setChecked(event.target.checked)}
                        autoFocus
                    />
                )}
                {error && (
                    <Alert
                        variant="danger"
                        className="mt-3 mb-0"
                        ref={errorRef}
                        tabIndex={-1}
                    >
                        {error}
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <PreviewLockNote locked={locked} />
                <Button
                    variant="outline-primary"
                    onClick={close}
                    disabled={submitting}
                    autoFocus={!needsConfirm}
                >
                    Cancel
                </Button>
                {offerChanged ? (
                    <Button variant="primary" onClick={onChanged}>
                        Reload
                    </Button>
                ) : (
                    <Button
                        variant="primary"
                        onClick={confirm}
                        disabled={submitting || locked || acceptBlocked}
                        aria-describedby={
                            locked ? PREVIEW_LOCK_NOTE_ID : undefined
                        }
                    >
                        {submitting && (
                            <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                                aria-hidden="true"
                            />
                        )}
                        Accept
                        {submitting && (
                            <span className="visually-hidden">
                                {' '}
                                in progress
                            </span>
                        )}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}

// Declining an offer and leaving an accepted position both end the student's
// place and both ask why. The reason reaches the program's coordinators only.
function EndDialog({
    kind,
    heading,
    candidacy,
    shownStage,
    onDone,
    onCancel,
    onChanged,
}) {
    const [submitting, setSubmitting] = useState(false);
    const [failure, setFailure] = useState(null);
    const inFlight = useRef(false);
    const leaving = kind === 'leave';

    const confirm = async (reason) => {
        if (inFlight.current) return;
        inFlight.current = true;
        setSubmitting(true);
        setFailure(null);
        try {
            const updated = leaving
                ? (
                      await candidacyService.release(candidacy.id, reason, {
                          stage: shownStage,
                      })
                  ).candidacy
                : await candidacyService.respond(
                      candidacy.id,
                      'decline',
                      candidacy.offer.offeredAt,
                      reason
                  );
            onDone(updated);
        } catch (err) {
            setFailure({
                message: err?.response?.data?.message || FALLBACK_ERROR,
                conflict: err?.response?.status === 409,
            });
        } finally {
            inFlight.current = false;
            setSubmitting(false);
        }
    };

    const refusal = failure && (
        <>
            <p className="mb-0">{failure.message}</p>
            {failure.conflict && (
                <Button
                    variant="outline-danger"
                    size="sm"
                    className="mt-2"
                    onClick={onChanged}
                >
                    Reload
                </Button>
            )}
        </>
    );

    return (
        <ReleaseReasonDialog
            show
            side="student"
            title={
                leaving
                    ? `Leave the LA position for ${heading}?`
                    : `Decline ${heading}?`
            }
            intro={
                leaving
                    ? "Your assignment ends and your place opens for another student. This can't be undone here."
                    : "Your place goes to another student. You can't undo this or apply to this position again."
            }
            confirmLabel={leaving ? 'Leave this position' : 'Decline'}
            busy={submitting}
            refusal={refusal}
            onConfirm={confirm}
            onHide={onCancel}
        />
    );
}

// The reviewer's text as the email shows it: split at blank lines, each piece
// trimmed, empty pieces dropped.
const messageParagraphs = (message) =>
    typeof message === 'string'
        ? message
              .replace(/\r\n/g, '\n')
              .split(/\n[ \t]*\n/)
              .map((piece) => piece.trim())
              .filter(Boolean)
        : [];

function LeaveMenu({ heading, disabled, onLeave }) {
    const toggleId = useId();
    return (
        <Dropdown align="end">
            <Dropdown.Toggle
                variant="outline-primary"
                size="sm"
                className="position-action"
                id={toggleId}
                aria-label={`More actions for ${heading}`}
            >
                More
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <Dropdown.Item
                    as="button"
                    type="button"
                    disabled={disabled}
                    className="text-danger"
                    onClick={onLeave}
                >
                    Leave this position
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );
}

/**
 * An offer the student can answer: who and when, what accepting commits them
 * to, then Accept and Decline, each asking first (Decline asks why). Once
 * answered it shrinks to one line stating the result and takes focus; an
 * accept adds the next steps. An accepted position keeps Leave this position
 * in a More menu, which asks why too. A parent that passes `locked` renders
 * the preview-lock note itself.
 *
 * `acceptInfo` is { sessions, commitments, isReturning }: the program's
 * training dates and commitments and the student's new-or-returning answer.
 * `otherOffers` is how many other offers the student has open.
 */
function CandidacyOfferCard({
    candidacy,
    commitment,
    onAnswered,
    onChanged,
    locked,
    acceptInfo,
    otherOffers = 0,
}) {
    const previewLock = usePreviewLock();
    const isLocked = locked ?? previewLock;
    const [dialog, setDialog] = useState(null);
    const [result, setResult] = useState(null);
    const [focusResult, setFocusResult] = useState(false);
    const resultRef = useRef(null);

    const stage =
        ['offered', 'accepted'].includes(candidacy.stage) && result
            ? result
            : candidacy.stage;

    useEffect(() => {
        if (focusResult && resultRef.current) {
            resultRef.current.focus();
            setFocusResult(false);
        }
    }, [focusResult, stage]);

    const shown =
        ['offered', 'expired', 'accepted', 'declined'].includes(stage) ||
        (stage === 'released' && result === 'released');
    if (!shown) return null;

    const { position } = candidacy;
    const heading = positionHeading(position);
    const shortName = positionShortName(position);
    const picture = hoursPicture(commitment, candidacy.program);
    const modality = modalityText(position?.modality);
    const offerText = messageParagraphs(candidacy.offer?.message);
    const details = acceptDetails({
        candidacy,
        picture,
        commitment,
        acceptInfo,
        others: otherOffers,
    });
    const facts = [
        position?.instructorName && `Instructor: ${position.instructorName}`,
        position?.crn && `CRN ${position.crn}`,
        modality,
    ].filter(Boolean);
    const justAccepted = stage === 'accepted' && result === 'accepted';

    // Leaving frees the hours the place held, so the commitments reload as
    // they do after an accept. The list keeps `released` among the stages it
    // mounts this card for, so the card and its focused result survive the
    // parent taking the released candidacy.
    const done = (updated) => {
        setResult(updated.stage);
        setFocusResult(true);
        setDialog(null);
        onAnswered?.(updated);
        if (['accepted', 'released'].includes(updated.stage)) onChanged?.();
    };

    const lockNote = locked === undefined && (
        <div className="mt-2">
            <PreviewLockNote locked={isLocked} />
        </div>
    );

    return (
        <Card
            className={stage === 'offered' ? 'border mt-2' : 'border-0 mt-1'}
            role="group"
            aria-label={`Offer for ${shortName}`}
        >
            <Card.Body className={stage === 'offered' ? '' : 'px-0 py-1'}>
                {stage === 'offered' ? (
                    <>
                        {candidacy.offer?.respondBy && (
                            <p className="fw-semibold mb-1">
                                Answer by{' '}
                                {formatAnswerBy(candidacy.offer.respondBy)}
                            </p>
                        )}
                        {facts.length > 0 && (
                            <p className="mb-2 text-body-secondary">
                                {facts.join(' · ')}
                            </p>
                        )}
                        <IfYouAccept items={details.items} commitments={[]} />
                        {offerText.length > 0 && (
                            <div className="mb-3">
                                <h3 className="h6">Message with your offer</h3>
                                {offerText.map((paragraph, index) => (
                                    <p key={index} className="mb-1 text-break">
                                        {paragraph}
                                    </p>
                                ))}
                            </div>
                        )}
                        <div className="d-flex flex-wrap gap-2">
                            <Button
                                variant="primary"
                                className="flex-grow-1 flex-sm-grow-0 position-action"
                                disabled={isLocked}
                                aria-describedby={
                                    isLocked ? PREVIEW_LOCK_NOTE_ID : undefined
                                }
                                onClick={() => setDialog('accept')}
                            >
                                Accept
                                <span className="visually-hidden">
                                    {' '}
                                    {heading}
                                </span>
                            </Button>
                            <Button
                                variant="outline-danger"
                                className="flex-grow-1 flex-sm-grow-0 position-action"
                                disabled={isLocked}
                                aria-describedby={
                                    isLocked ? PREVIEW_LOCK_NOTE_ID : undefined
                                }
                                onClick={() => setDialog('decline')}
                            >
                                Decline
                                <span className="visually-hidden">
                                    {' '}
                                    {heading}
                                </span>
                            </Button>
                        </div>
                        {lockNote}
                    </>
                ) : (
                    <>
                        <div role="status" tabIndex={-1} ref={resultRef}>
                            {stage === 'accepted' && (
                                <>
                                    <p className="fw-semibold mb-1">
                                        {justAccepted
                                            ? `You accepted ${shortName}. Congratulations!`
                                            : 'You accepted this position.'}
                                    </p>
                                    {candidacy.program === 'la' &&
                                        !candidacy.onboardingComplete && (
                                            <p className="mb-1">
                                                The LA program will contact you
                                                about onboarding so you can be
                                                hired.
                                            </p>
                                        )}
                                    {justAccepted && (
                                        <ul className="mb-2">
                                            <li>
                                                Your position and start date are
                                                on My Assignments.
                                            </li>
                                            {details.training.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    )}
                                    {candidacy.program === 'la' && (
                                        <LaContractTask
                                            name={
                                                candidacy.position
                                                    ?.instructorName
                                            }
                                        />
                                    )}
                                </>
                            )}
                            {stage === 'declined' && (
                                <p className="fw-semibold mb-0">
                                    You declined this offer.
                                </p>
                            )}
                            {stage === 'expired' && (
                                <p className="fw-semibold mb-0">
                                    {`This offer expired${
                                        candidacy.offer?.respondBy
                                            ? ` on ${formatAnswerDay(candidacy.offer.respondBy)}`
                                            : ''
                                    } because it was not answered. The program may offer it again.`}
                                </p>
                            )}
                            {stage === 'released' && (
                                <p className="fw-semibold mb-0">
                                    You left this position.
                                </p>
                            )}
                        </div>
                        {stage === 'accepted' && (
                            <div className="mt-1 d-flex flex-wrap align-items-center gap-3">
                                <Link to="/assignments">View assignment</Link>
                                <LeaveMenu
                                    heading={shortName}
                                    disabled={isLocked}
                                    onLeave={() => setDialog('leave')}
                                />
                                {lockNote}
                            </div>
                        )}
                    </>
                )}
            </Card.Body>
            {dialog === 'accept' && (
                <AcceptDialog
                    heading={heading}
                    picture={picture}
                    locked={isLocked}
                    candidacy={candidacy}
                    details={details}
                    onDone={done}
                    onCancel={() => setDialog(null)}
                    onChanged={() => {
                        setDialog(null);
                        onChanged?.();
                    }}
                />
            )}
            {(dialog === 'decline' || dialog === 'leave') && (
                <EndDialog
                    kind={dialog}
                    heading={heading}
                    candidacy={candidacy}
                    shownStage={stage}
                    onDone={done}
                    onCancel={() => setDialog(null)}
                    onChanged={() => {
                        setDialog(null);
                        onChanged?.();
                    }}
                />
            )}
        </Card>
    );
}

export default CandidacyOfferCard;
